import { useMemo, useState } from "react";
import { scarpaGray400, spanishViridian500 } from "@habitech/shared";
import Icon from "src/components/common/Icon";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
    selectCommercialCartSelection,
    selectDataSuggested,
    selectHasInputErrors,
    selectSelectedMonths,
    selectSelectedPaymentMethod,
} from "src/redux/shopping_cart/shopping_cart.selectors";
import { toggleProductSelection } from "src/redux/shopping_cart/shopping_cart.slice";
import { ProductByCategory } from "src/redux/shopping_cart/shopping_cart.initial";
import { ORDERED_CATEGORIES_FOR_DISPLAY } from "../ProducsSelectable/CommercialCart.constants";
import { payment_method, pyament_method_str } from "src/constants/ShoppingCart";
import ButtonPaymentOneClick from "src/components/ButtonPaymentOneClick/ButtonPaymentOneClick";
import {
    Card,
    CheckboxRow,
    Divider,
    EmptyState,
    Header,
    PaymentRow,
    PeriodChip,
    ProductList,
    ProductRow,
    TotalRow,
} from "./OrderSummary.styles";
import { selectUser } from "src/redux/user/userSlice";
import { useSelector } from "react-redux";

function getPaymentMethodLabel(value: number): string {
    const entry = Object.entries(payment_method).find(([, v]) => v === value);
    if (!entry) return "Sin seleccionar";
    const key = entry[0] as keyof typeof pyament_method_str;
    return pyament_method_str[key] ?? "Sin seleccionar";
}

const OrderSummary = () => {
    const dispatch = useAppDispatch();
    const user = useSelector(selectUser);
    const { suggested } = useAppSelector(selectDataSuggested);
    const { selectedByProductId, quantityByProductId, priceByProductId } = useAppSelector(selectCommercialCartSelection);
    const selectedMonths = useAppSelector(selectSelectedMonths);
    const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
    const hasInputErrors = useAppSelector(selectHasInputErrors);
    const [disclaimerChecked, setDisclaimerChecked] = useState(false);

    const getAllProducts = (): ProductByCategory[] =>
        Object.values(suggested.products.category).flat();

    const selectedProductsRaw = getAllProducts().filter((p) => selectedByProductId[p.product_id]);
    const selectedProducts = [...selectedProductsRaw].sort((a, b) => {
        const indexA = ORDERED_CATEGORIES_FOR_DISPLAY.indexOf(a.category);
        const indexB = ORDERED_CATEGORIES_FOR_DISPLAY.indexOf(b.category);
        if (indexA !== indexB) return indexA - indexB;
        return a.product_id - b.product_id;
    });
    const total = selectedProducts.reduce(
        (sum, p) => sum + (priceByProductId[p.product_id] ?? 0),
        0
    );
    const periodLabel = selectedMonths === null ? "Sin periodo" : `${selectedMonths} meses`;
    const paymentLabel =
        selectedPaymentMethod === null ? "Sin seleccionar" : getPaymentMethodLabel(selectedPaymentMethod);

    const canSubmit =
        selectedProducts.length > 0 &&
        selectedMonths !== null &&
        selectedPaymentMethod !== null &&
        disclaimerChecked &&
        !hasInputErrors;
    
    const hasPremium = selectedProducts.some(
        (p) => (p.subcategory ?? p.category)?.toLowerCase() === "premium"
    );

    const paymentDetail = useMemo(() => {
        if (selectedMonths === null) return "[]";
        return JSON.stringify(
            selectedProducts.map((product) => {
                const quantity = quantityByProductId[product.product_id] ?? 0;
                const item: Record<string, unknown> = {
                    cart_product_id: product.product_id,
                    category: product.category,
                    months: selectedMonths,
                    quantity,
                    price: priceByProductId[product.product_id] ?? 0,
                    description: `${quantity} ${product.name_product} por ${selectedMonths} ${selectedMonths === 1 ? "mes" : "meses"}`,
                    user_id: user?.id,
                };
                if (product.category === "Destacado") item.classification_id = 1;
                if (product.category === "Leads") item.quantity_paid_leads = "99999";
                if (product.category === "smart_leads") item.months  = 12;
                return item;
            })
        );
    }, [selectedProducts, selectedMonths, quantityByProductId, priceByProductId, user?.id]);

    const handleRemove = (product: ProductByCategory) => {
        dispatch(
            toggleProductSelection({
                productId: product.product_id,
                willBeSelected: false,
                productIdsToClear: [product.product_id],
            })
        );
    };

    return (
        <Card>
            <Header>
                <h2 className="header-title">
                    <Icon icon="pcom-icon-cart" size={22} style={{ color: spanishViridian500 }} />
                    Tu pedido
                </h2>
                <PeriodChip>{periodLabel}</PeriodChip>
            </Header>

            {selectedProducts.length === 0 ? (
                <EmptyState>
                    <Icon icon="pcom-icon-cart" size={48} style={{ color: scarpaGray400 }} />
                    <span>Agrega productos</span>
                </EmptyState>
            ) : (
                <ProductList>
                    {selectedProducts.map((product) => (
                        <ProductRow key={product.product_id}>
                            <div className="product-info">
                                <span className="product-name">{product.name_product}</span>
                                <span className="product-quantity">
                                    {quantityByProductId[product.product_id] ?? 0} cantidad
                                </span>
                            </div>
                            <span className="product-price">
                                ${priceByProductId[product.product_id] ?? 0}
                            </span>
                            <button
                                type="button"
                                className="remove-btn"
                                onClick={() =>{
                                    if (hasPremium && product.subcategory?.toLowerCase() === "leads") return;
                                    handleRemove(product)
                                }}
                                aria-label={`Quitar ${product.name_product}`}
                            >
                                <Icon icon="pcom-icon-close" size={10} />
                            </button>
                        </ProductRow>
                    ))}
                </ProductList>
            )}

            <Divider />
            <TotalRow>
                <span className="total-label">Total</span>
                <span className="total-amount">${total}</span>
            </TotalRow>
            <Divider />
            <PaymentRow>
                <span className="payment-label">
                    <Icon icon="pcom-icon-credit-card-2" size={18} style={{ color: spanishViridian500 }} />
                    Pago
                </span>
                <span className="payment-value">{paymentLabel}</span>
            </PaymentRow>
            <CheckboxRow>
                <input
                    type="checkbox"
                    checked={disclaimerChecked}
                    onChange={(e) => setDisclaimerChecked(e.target.checked)}
                />
                <span>
                    Confirmo que esta venta cumple con lineamientos comerciales y regulatorios
                </span>
            </CheckboxRow>
            <ButtonPaymentOneClick
                total={total}
                description="Carrito comercial"
                detail={paymentDetail}
                buttonDisabled={!canSubmit}
                user_id={suggested.users.user_id}
                type_payment={selectedPaymentMethod ?? undefined}
            />
        </Card>
    );
};

export default OrderSummary;
